<template>
  <CModal
    :show.sync="show"
    @close="close"
    size="lg"
  >
    <template #header>
      <h5 class="modal-title" v-translate translate-context="payout">Request payout</h5>
      <CButtonClose @click="close" />
    </template>
    
    <template #default>
      <CForm @submit.prevent="handleSubmit">
        <CRow>
          <CCol lg="12">
            <CAlert color="info" class="mb-4">
              <div v-if="preparedPayout" class="mb-2">
                <strong v-translate translate-context="payout">Net Amount:</strong>
                {{ preparedPayout.paid_net | currency(contract.currency) }}
              </div>
              <div v-if="preparedPayout" class="mb-2 small">
                <translate translate-context="payout">Gross:</translate> {{ preparedPayout.paid_gross | currency(contract.currency) }} | 
                <translate translate-context="payout">Fees:</translate> {{ preparedPayout.pica_fees | currency(contract.currency) }}
              </div>
              <div v-if="preparedPayout" class="mb-2 small">
                <CIcon name="cipInfo" size="sm" class="mr-1"/>
                <translate translate-context="payout">This amount has been frozen and will not change</translate>
              </div>
              <div v-if="preparedPayout && preparedPayout.date_from && preparedPayout.date_to" class="mb-2 small">
                <strong v-translate translate-context="payout">Period:</strong>
                {{ formatDate(preparedPayout.date_from) }} - {{ formatDate(preparedPayout.date_to) }}
              </div>
              <div v-if="preparedPayout && preparedPayout.photos_sold" class="mb-2 small">
                <translate translate-context="payout">Photos sold:</translate> {{ preparedPayout.photos_sold }} | 
                <translate translate-context="payout">Orders:</translate> {{ preparedPayout.num_orders_total }}
              </div>
              <div v-if="preparedPayout && preparedPayout.events_count" class="mb-2 small">
                <translate translate-context="payout">Events:</translate> {{ preparedPayout.events_count }}
              </div>
            </CAlert>
          </CCol>
        </CRow>
        
        
        <CRow>
          <CCol lg="12">
            <div class="form-group">
              <label class="required" v-translate translate-context="payout">IBAN</label>
              <CInput
                v-model="form.iban"
                type="text"
                maxlength="34"
                :placeholder="$pgettext('payout', 'Enter your IBAN')"
                required
                :class="{'is-invalid': validation.iban === false}"
              />
              <div v-if="validation.iban === false" class="invalid-feedback d-block">
                <translate translate-context="payout">Invalid IBAN format</translate>
              </div>
            </div>
          </CCol>
        </CRow>
        
        <CRow>
          <CCol lg="12">
            <div class="form-group">
              <div class="custom-control custom-checkbox">
                <input
                  type="checkbox"
                  class="custom-control-input"
                  id="save-iban-checkbox"
                  v-model="form.save_iban"
                />
                <label class="custom-control-label" for="save-iban-checkbox">
                  <translate translate-context="payout">Save IBAN for future payouts</translate>
                </label>
              </div>
            </div>
          </CCol>
        </CRow>
        
        <CRow>
          <CCol lg="12">
            <div class="form-group">
              <label class="required" v-translate translate-context="payout">Invoice</label>
              <div v-if="!form.invoice" class="custom-file-upload" :class="{'is-invalid': validation.invoice === false}">
                <input
                  type="file"
                  ref="fileInput"
                  @change="handleFileChange"
                  accept=".pdf,.jpg,.jpeg,.png"
                  class="d-none"
                />
                <CButton
                  color="primary"
                  variant="outline"
                  @click="$refs.fileInput.click()"
                  class="w-100"
                >
                  <CIcon name="cipUpload" size="sm" class="mr-2"/>
                  <translate translate-context="payout">Upload invoice</translate>
                </CButton>
              </div>
              
              <div v-else class="uploaded-file">
                <CCard>
                  <CCardBody class="py-2 px-3 d-flex align-items-center justify-content-between">
                    <div class="d-flex align-items-center">
                      <CIcon name="cipFile03" size="lg" class="mr-2 text-primary"/>
                      <span>{{ form.invoice.name }}</span>
                    </div>
                    <CButton
                      color="danger"
                      size="sm"
                      variant="ghost"
                      @click="removeFile"
                    >
                      <CIcon name="cipClose" size="sm"/>
                    </CButton>
                  </CCardBody>
                </CCard>
              </div>
              <div v-if="validation.invoice === false" class="invalid-feedback d-block">
                <translate translate-context="payout">Invoice is required</translate>
              </div>
            </div>
          </CCol>
        </CRow>
        
        <CRow>
          <CCol lg="12">
            <div class="form-group">
              <label v-translate translate-context="payout">Notes (optional)</label>
              <CTextarea
                v-model="form.notes"
                :rows="3"
                :placeholder="$pgettext('payout', 'Add any additional notes')"
              />
            </div>
          </CCol>
        </CRow>
        
      </CForm>
    </template>
    
    <template #footer>
      <CButton color="secondary" variant="outline" @click="close">
        <translate translate-context="payout">Cancel</translate>
      </CButton>
      <CButton
        color="primary"
        @click="handleSubmit"
        :disabled="!isValid || loading"
      >
        <CSpinner v-if="loading" size="sm" class="mr-2"/>
        <translate translate-context="payout">Submit request</translate>
      </CButton>
    </template>
  </CModal>
</template>

<script>
import moment from 'moment'
import { mapActions } from 'vuex'

export default {
  name: 'PayoutRequestModal',
  props: {
    contract: {
      type: Object,
      default: () => ({})
    },
    statistics: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      show: false,
      preparedPayout: null, // Payout già cristallizzato
      form: {
        iban: '',
        save_iban: false,
        invoice: null,
        notes: ''
      },
      validation: {
        iban: null,
        invoice: null
      },
      loading: false
    }
  },
  computed: {
    isValid() {
      // Don't validate IBAN in real-time to avoid focus issues
      // Just check if fields are filled
      return this.form.iban && 
             this.form.invoice !== null
    }
  },
  methods: {
    ...mapActions('payout', ['requestPayout']),
    open(payout) {
      // Main method to open the modal - follows the pattern of other modals
      this.openWithPreparedPayout(payout)
    },
    openWithPreparedPayout(payout) {
      this.preparedPayout = payout
      this.resetForm()
      
      // Pre-fill IBAN from payout or from saved config
      if (payout && payout.iban) {
        this.form.iban = payout.iban
      } else if (this.statistics && this.statistics.raw && this.statistics.raw.config && this.statistics.raw.config.saved_iban) {
        // Use saved IBAN from statistics config
        this.form.iban = this.statistics.raw.config.saved_iban
      } else if (this.statistics && this.statistics.config && this.statistics.config.saved_iban) {
        // Alternative path if statistics structure is different
        this.form.iban = this.statistics.config.saved_iban
      }
      
      // Open the modal
      this.show = true
    },
    
    formatDate(dateString) {
      return moment(dateString).format('DD/MM/YYYY')
    },
    
    resetForm() {
      this.form = {
        iban: '',
        save_iban: false,
        invoice: null,
        notes: ''
      }
      this.validation = {
        iban: null,
        invoice: null
      }
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    },
    
    validateIBAN(iban) {
      // Basic IBAN validation
      if (!iban) return false
      const cleanIban = iban.replace(/\s/g, '').toUpperCase()
      // Check basic format: 2 letters, 2 digits, then alphanumeric
      const ibanRegex = /^[A-Z]{2}[0-9]{2}[A-Z0-9]+$/
      // Also check minimum length (15 chars is shortest valid IBAN)
      return ibanRegex.test(cleanIban) && cleanIban.length >= 15
    },
    
    handleFileChange(event) {
      const file = event.target.files[0]
      if (file) {
        const maxSize = 10 * 1024 * 1024 // 10MB
        if (file.size > maxSize) {
          this.notifyError(this.$pgettext('payout', 'File size cannot exceed 10MB'))
          return
        }
        
        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png']
        if (!allowedTypes.includes(file.type)) {
          this.notifyError(this.$pgettext('payout', 'File format not supported. Use PDF, JPG or PNG'))
          return
        }
        
        this.form.invoice = file
        this.validation.invoice = true
      }
    },
    
    removeFile() {
      this.form.invoice = null
      this.$refs.fileInput.value = ''
    },
    
    async handleSubmit() {
      if (!this.validate()) {
        return
      }
      
      this.loading = true
      try {
        // Richiede il pagamento fornendo fattura, IBAN e note
        await this.requestPayout({
          payoutId: this.preparedPayout.id,
          data: this.form
        })
        
        this.$emit('success')
        this.close()
      } catch (error) {
        this.notifyError(this.$pgettext('payout', 'Error requesting payout'))
      } finally {
        this.loading = false
      }
    },
    
    validate() {
      let isValid = true
      
      // Validate IBAN
      if (!this.form.iban) {
        this.validation.iban = false
        isValid = false
      } else if (!this.validateIBAN(this.form.iban)) {
        this.validation.iban = false
        isValid = false
      } else {
        this.validation.iban = true
      }
      
      // Validate invoice
      if (!this.form.invoice) {
        this.validation.invoice = false
        isValid = false
      } else {
        this.validation.invoice = true
      }
      
      return isValid
    },
    
    close() {
      this.show = false
      this.preparedPayout = null
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.custom-file-upload {
  border: 2px dashed #ddd;
  border-radius: 4px;
  padding: 20px;
  text-align: center;
  transition: all 0.3s;
}

.custom-file-upload:hover {
  border-color: var(--primary);
}

.custom-file-upload.is-invalid {
  border-color: #dc3545;
}

.uploaded-file {
  margin-top: 10px;
}
</style>